import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getSupportHistory,
    deleteSupportTicket,
    createSupportTicket,
    updateSupportTicket,
} from "./api";
import type { ISupportTicket, ISupportPaginated } from "./types";

export const supportKeys = {
    root: ["support"] as const,
    list: (filters?: Record<string, any>) =>
        [...supportKeys.root, "list", filters] as const,
    detail: (id: string) => [...supportKeys.root, "detail", id] as const,
};


/**
 * Fetch support tickets (all or filtered)
 */
/* No arguments: the endpoint takes none that it honours, and passing
   `(1, 10, filters)` here silently capped the app at ten tickets with no pager
   to reach the rest. */
export function useSupportHistory() {
    return useQuery<ISupportPaginated<ISupportTicket>>({
        queryKey: supportKeys.list({}),
        queryFn: () => getSupportHistory(),
        placeholderData: (prev) => prev,
        staleTime: 30_000,
    });
}


/**
 * Create a new support ticket
 */
export function useCreateSupportTicket() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createSupportTicket,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: supportKeys.root });
        },
    });
}


/**
 * Update a support ticket
 */
export function useUpdateSupportTicket() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string;
            data: Partial<ISupportTicket>;
        }) => updateSupportTicket(id, data),
        onSuccess: (_, variables) => {
            // invalidate both detail and list caches
            queryClient.invalidateQueries({
                queryKey: supportKeys.detail(variables.id),
            });
            queryClient.invalidateQueries({ queryKey: supportKeys.root });
        },
    });
}


/**
 * Delete a support ticket
 */
export function useDeleteSupportTicket() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteSupportTicket(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: supportKeys.root });
        },
    });
}
